import { Workshop } from '../api/workshop/workshop.model';
import { Camp } from '../api/camp/camp.model';
import { Course } from '../api/course/course.model';
import {
  ApiQueryParams,
  FilterOperator,
  FilterSuffix,
  mergeApiParams,
} from '../api/api.operators';
import { isCourse } from '../api/course/course.util';
import { isCamp } from '../api/camp/lib/camp.util';
import { isWorkshop } from '../api/workshop/workshop.util';
import { Activity } from '../api/activity/activity.model';
import { BeehiveApiService } from '../api/beehive-api.service';
import { forkJoin, map, Observable } from 'rxjs';
import { ApiResource } from '../api/api-resource';
import { getScheduleInterval } from '../api/_lib/schedule';
import { arrayUniqueBy } from './array';
import { Staff } from '../api/staff/staff.model';
import { ActivityRole } from '../api/activity/lib/activity.interfaces';
import { getPageMetadata, setPageMetadata } from './metadata';
import { Pipe, PipeTransform } from '@angular/core';
import { extractDateLabel } from './date';

export interface HasTarget {
  camp?: Camp | null;
  workshop?: Workshop | null;
  course?: Course | null;
  target: Target;
  rootTarget: RootTarget;
}

export type Target = Camp | Course | Workshop;
export type BaseTarget = Camp & Course & Workshop;
export type RootTarget = Camp | Workshop;
export type BaseRootTarget = Camp & Workshop;

export function getKeyofTarget(target: Target) {
  return getTargetKey({
    course: target as Course,
    camp: target as Camp,
    workshop: target as Workshop,
  } as HasTarget);
}

export function getTargetKey(
  hasTarget: HasTarget,
): 'course' | 'camp' | 'workshop' {
  if (isCourse(hasTarget.course)) return 'course';
  if (isCamp(hasTarget.camp)) return 'camp';
  if (isWorkshop(hasTarget.workshop)) return 'workshop';
  throw new Error(
    'Missing one of three required fields camp, course, or workshop',
  );
}

export function getRootTargetKey(
  hasTarget: HasTarget,
): 'course.camp' | 'camp' | 'workshop' {
  if (isCourse(hasTarget.course)) return 'course.camp';
  if (isCamp(hasTarget.camp)) return 'camp';
  if (isWorkshop(hasTarget.workshop)) return 'workshop';
  throw new Error(
    'Missing one of three required fields camp, course, or workshop',
  );
}

export function getTargetFilter(
  data: HasTarget,
  field: keyof BaseTarget,
  operator: FilterOperator | FilterSuffix = FilterOperator.EQ,
) {
  const key = getTargetKey(data);
  return {
    [`${key}.${field}`]: [
      // !: getActivityTargetKey already asserts its presence.
      [operator, String((data.target as BaseTarget)![field])],
    ],
  };
}

export function getRootTargetFilter(
  data: HasTarget,
  field: keyof BaseRootTarget,
  operator: FilterOperator = FilterOperator.EQ,
) {
  const key = getRootTargetKey(data);
  return {
    [`${key}.${field}`]: [
      // !: getActivityRootTargetKey already asserts its presence.
      [operator, String((data.rootTarget as Workshop & Camp)![field])],
    ],
  };
}

export function wrapHasTarget(
  target: Target,
  staff?: Staff,
  role?: ActivityRole,
) {
  return {
    [getKeyofTarget(target)]: target,
    target,
    rootTarget: isCourse(target) ? target.camp : target,
    staff,
    role,
  } as unknown as Activity;
}

export function findStaffScheduleOverlap<
  T extends ApiResource<any> & HasTarget,
>(
  service: BeehiveApiService<T>,
  staff: Staff,
  resource: T,
  rawParams?: ApiQueryParams<T>,
): Observable<T[]> {
  const { start, end } = getScheduleInterval(resource.rootTarget.schedule);
  return forkJoin(
    ['course.camp', 'camp', 'workshop'].map((key) =>
      service.list(
        mergeApiParams(rawParams, {
          filters: {
            ...(resource.id ? { id: [FilterSuffix.NOT, resource.id] } : {}),
            'staff.id': [FilterOperator.EQ, staff.id],
            // Same period
            [`${key}.start`]: [FilterOperator.LTE, end],
            [`${key}.end`]: [FilterOperator.GTE, start],
          },
        }),
      ),
    ),
  ).pipe(
    map((arrays) =>
      setPageMetadata(
        arrayUniqueBy(arrays.flat(), (el) => el.id),
        {
          totalItems: Math.max(
            ...arrays.map((a) => getPageMetadata(a).totalItems),
          ),
        },
      ),
    ),
  );
}

@Pipe({
  name: 'targetStart',
  standalone: true,
})
export class TargetStartPipe implements PipeTransform {
  transform(target: Camp | Workshop): string {
    return extractDateLabel(getScheduleInterval(target.schedule).start);
  }
}
